/**
 * BotSpot Web Dashboard - Credential Form Component
 *
 * Form for creating and updating credentials with validation.
 */

import React, { useState } from "react";
import type { Credential, CredentialProvider } from "../api/types";
import { YELLOW, PIXEL, MONO } from "../theme";

/**
 * Props interface
 */
interface CredentialFormProps {
  credential?: Credential;
  onSubmit: (data: {
    provider: CredentialProvider;
    name: string;
    credential_value: string;
    expires_at?: string;
  }) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

/**
 * Credential Form Component
 *
 * Provides a form interface for creating and editing credentials
 * with validation and error handling.
 */
export function CredentialForm({
  credential,
  onSubmit,
  onCancel,
  loading = false,
}: CredentialFormProps) {
  const [provider, setProvider] = useState<string>(
    credential?.provider || "claude"
  );
  const [name, setName] = useState(credential?.name || "");
  const [credentialValue, setCredentialValue] = useState("");
  const [expiresAt, setExpiresAt] = useState(
    credential?.expires_at
      ? new Date(credential.expires_at).toISOString().split("T")[0]
      : ""
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Validate form data
   */
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = "Name is required";
    } else if (name.trim().length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    }

    if (!credential && !credentialValue.trim()) {
      newErrors.credential_value = "Credential value is required";
    } else if (credentialValue && credentialValue.trim().length < 8) {
      newErrors.credential_value =
        "Credential value must be at least 8 characters";
    }

    if (expiresAt) {
      const expiryDate = new Date(expiresAt);
      const now = new Date();
      if (expiryDate <= now) {
        newErrors.expires_at = "Expiry date must be in the future";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const data: {
        provider: CredentialProvider;
        name: string;
        credential_value: string;
        expires_at?: string;
      } = {
        provider: provider as CredentialProvider,
        name: name.trim(),
        credential_value: credentialValue || "",
      };

      if (expiresAt) {
        data.expires_at = new Date(expiresAt).toISOString();
      }

      await onSubmit(data);
    } catch (error) {
      // Error handling is done by parent component
    }
  };

  return (
    <form style={styles.form} onSubmit={handleSubmit}>
      <h2 style={styles.title}>
        {credential ? "Update Credential" : "Add New Credential"}
      </h2>

      {/* Provider Select */}
      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="provider">
          Provider *
        </label>
        <select
          id="provider"
          style={styles.select}
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          disabled={loading || !!credential}
        >
          <option value="claude">Claude AI</option>
          <option value="openai">OpenAI</option>
          <option value="discord">Discord</option>
          <option value="telegram">Telegram</option>
          <option value="slack">Slack</option>
          <option value="github">GitHub</option>
          <option value="custom">Custom</option>
        </select>
        {credential && (
          <p style={styles.helpText}>
            Provider cannot be changed after creation
          </p>
        )}
      </div>

      {/* Name Input */}
      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="name">
          Name *
        </label>
        <input
          id="name"
          type="text"
          style={{
            ...styles.input,
            ...(errors.name ? styles.inputError : {}),
          }}
          placeholder="e.g., My Claude API Key"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={loading}
        />
        {errors.name && <p style={styles.errorText}>{errors.name}</p>}
        <p style={styles.helpText}>A friendly name to identify this credential</p>
      </div>

      {/* Credential Value Input */}
      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="credential_value">
          {credential ? "New Credential Value" : "Credential Value *"}
        </label>
        <input
          id="credential_value"
          type="password"
          style={{
            ...styles.input,
            ...(errors.credential_value ? styles.inputError : {}),
          }}
          placeholder="Enter API key or token"
          value={credentialValue}
          onChange={(e) => setCredentialValue(e.target.value)}
          disabled={loading}
        />
        {errors.credential_value && (
          <p style={styles.errorText}>{errors.credential_value}</p>
        )}
        {credential ? (
          <p style={styles.helpText}>
            Leave blank to keep existing value
          </p>
        ) : (
          <p style={styles.helpText}>
            This value is encrypted and cannot be retrieved later
          </p>
        )}
      </div>

      {/* Expiry Date Input */}
      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="expires_at">
          Expiry Date (Optional)
        </label>
        <input
          id="expires_at"
          type="date"
          style={{
            ...styles.input,
            ...(errors.expires_at ? styles.inputError : {}),
          }}
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          disabled={loading}
        />
        {errors.expires_at && (
          <p style={styles.errorText}>{errors.expires_at}</p>
        )}
        <p style={styles.helpText}>
          Optional expiry date for automatic credential rotation
        </p>
      </div>

      {/* Action Buttons */}
      <div style={styles.buttonGroup}>
        <button
          type="button"
          style={styles.cancelButton}
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          style={{
            ...styles.submitButton,
            ...(loading ? styles.buttonDisabled : {}),
          }}
          disabled={loading}
        >
          {loading ? "Saving..." : credential ? "Update" : "Create"}
        </button>
      </div>
    </form>
  );
}

/**
 * Inline styles
 */
const styles: Record<string, React.CSSProperties> = {
  form: {
    backgroundColor: "rgba(0,106,167,0.06)",
    border: "1px solid rgba(0,106,167,0.2)",
    padding: "2rem",
    maxWidth: "600px",
    margin: "0 auto",
  },
  title: {
    margin: "0 0 1.5rem 0",
    fontFamily: PIXEL,
    fontSize: "0.7rem",
    fontWeight: 400,
    color: YELLOW,
    letterSpacing: "0.08em",
    textShadow: "0 0 10px rgba(254,204,0,0.3)",
  },
  formGroup: {
    marginBottom: "1.5rem",
  },
  label: {
    display: "block",
    marginBottom: "0.5rem",
    fontFamily: PIXEL,
    fontSize: "0.42rem",
    fontWeight: 500,
    color: YELLOW,
    letterSpacing: "0.15em",
    textShadow: "0 0 8px rgba(254,204,0,0.3)",
  },
  input: {
    width: "100%",
    padding: "0.75rem",
    fontFamily: MONO,
    backgroundColor: "rgba(0,106,167,0.06)",
    border: "1px solid rgba(0,106,167,0.25)",
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.87)",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  select: {
    width: "100%",
    padding: "0.75rem",
    fontFamily: MONO,
    backgroundColor: "rgba(0,106,167,0.06)",
    border: "1px solid rgba(0,106,167,0.25)",
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.87)",
    boxSizing: "border-box",
    cursor: "pointer",
  },
  inputError: {
    borderColor: "#ef4444",
  },
  errorText: {
    margin: "0.5rem 0 0 0",
    fontFamily: MONO,
    fontSize: "0.875rem",
    color: "#fca5a5",
  },
  helpText: {
    margin: "0.5rem 0 0 0",
    fontFamily: MONO,
    fontSize: "0.875rem",
    color: "rgba(255, 255, 255, 0.5)",
  },
  buttonGroup: {
    display: "flex",
    gap: "1rem",
    justifyContent: "flex-end",
    marginTop: "2rem",
  },
  cancelButton: {
    padding: "0.75rem 1.5rem",
    backgroundColor: "transparent",
    border: "1px solid rgba(0,106,167,0.3)",
    cursor: "pointer",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    color: "rgba(255, 255, 255, 0.87)",
    letterSpacing: "0.08em",
    transition: "background-color 0.2s",
  },
  submitButton: {
    padding: "0.75rem 1.5rem",
    backgroundColor: "transparent",
    border: `2px solid ${YELLOW}`,
    cursor: "pointer",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    color: YELLOW,
    letterSpacing: "0.1em",
    boxShadow: "0 0 10px rgba(254,204,0,0.2)",
    transition: "background-color 0.2s",
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
};
